import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Eye, Sparkle, ImageSquare } from "@phosphor-icons/react";

const riskClass = (r) => r === "alto" ? "risk-alto" : r === "medio" ? "risk-medio" : "risk-baixo";
const riskLabel = (r) => r === "alto" ? "ALTO" : r === "medio" ? "MÉDIO" : "BAIXO";

export default function Results() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ grau_risco: "", fonte: "", estado: "", status: "", is_demo: "" });
  const [search, setSearch] = useState("");

  const load = async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ""));
    if (params.is_demo === "real") { params.is_demo = false; } else if (params.is_demo === "demo") { params.is_demo = true; } else { delete params.is_demo; }
    const { data } = await api.get("/results", { params });
    setItems(data);
  };
  useEffect(() => { load(); }, [filters]);

  const analyze = async (id) => {
    toast.info("Analisando com IA...");
    try {
      await api.post(`/results/${id}/analyze`);
      toast.success("Análise atualizada");
      load();
    } catch (e) { toast.error("Falha na análise IA"); }
  };

  const visualAnalyze = async (r) => {
    if (!r.brand_id) { toast.error("Resultado sem marca vinculada"); return; }
    toast.info("Analisando visualmente...");
    try {
      await api.post(`/visual/compare`, { brand_id: r.brand_id, result_ids: [r.id] });
      toast.success("Análise visual concluída");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha na análise visual"); }
  };

  const filtered = items.filter(r => !search || r.nome_encontrado.toLowerCase().includes(search.toLowerCase()));

  const uniqueFontes = [...new Set(items.map(i => i.fonte))];
  const uniqueEstados = [...new Set(items.map(i => i.estado).filter(Boolean))];

  return (
    <div className="space-y-6" data-testid="results-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Resultados</div>
        <h1 className="font-heading font-extrabold text-4xl">Descobertas</h1>
      </div>

      <Card className="p-4 rounded-sm border">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <Label className="text-[10px] uppercase">Buscar</Label>
            <Input data-testid="results-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome..." className="rounded-sm h-9" />
          </div>
          <div>
            <Label className="text-[10px] uppercase">Origem</Label>
            <Select value={filters.is_demo || "all"} onValueChange={(v) => setFilters({ ...filters, is_demo: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-origin" className="rounded-sm h-9"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="real">Fonte real (SerpApi)</SelectItem>
                <SelectItem value="demo">Demonstrativo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase">Grau de risco</Label>
            <Select value={filters.grau_risco || "all"} onValueChange={(v) => setFilters({ ...filters, grau_risco: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-risk" className="rounded-sm h-9"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="alto">Alto</SelectItem>
                <SelectItem value="medio">Médio</SelectItem>
                <SelectItem value="baixo">Baixo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase">Fonte</Label>
            <Select value={filters.fonte || "all"} onValueChange={(v) => setFilters({ ...filters, fonte: v === "all" ? "" : v })}>
              <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {uniqueFontes.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase">Estado</Label>
            <Select value={filters.estado || "all"} onValueChange={(v) => setFilters({ ...filters, estado: v === "all" ? "" : v })}>
              <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {uniqueEstados.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase">Status</Label>
            <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
              <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="novo">Novo</SelectItem>
                <SelectItem value="revisado">Revisado</SelectItem>
                <SelectItem value="descartado">Descartado</SelectItem>
                <SelectItem value="conflito_confirmado">Conflito confirmado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F9FAFB]">
              <TableHead className="text-[10px] uppercase">Data</TableHead>
              <TableHead className="text-[10px] uppercase">Nome encontrado</TableHead>
              <TableHead className="text-[10px] uppercase">Fonte</TableHead>
              <TableHead className="text-[10px] uppercase">Cidade/UF</TableHead>
              <TableHead className="text-[10px] uppercase">Sim. nome</TableHead>
              <TableHead className="text-[10px] uppercase">Sim. visual</TableHead>
              <TableHead className="text-[10px] uppercase">Risco</TableHead>
              <TableHead className="text-[10px] uppercase">Status</TableHead>
              <TableHead className="text-[10px] uppercase text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(r => (
              <TableRow key={r.id} data-testid={`result-row-${r.id}`} className="hover:bg-muted/40">
                <TableCell className="text-xs font-mono">{new Date(r.data_descoberta).toLocaleDateString("pt-BR")}</TableCell>
                <TableCell className="text-sm font-semibold">
                  <div className="flex items-center gap-2">
                    <span>{r.nome_encontrado}</span>
                    {r.is_demo === false && <span className="text-[9px] px-1.5 py-0.5 bg-[#0A3B2C] text-white rounded-sm font-bold tracking-wider" data-testid="badge-real">REAL</span>}
                    {r.is_demo === true && <span className="text-[9px] px-1.5 py-0.5 border border-muted-foreground text-muted-foreground rounded-sm font-bold tracking-wider" data-testid="badge-demo">DEMO</span>}
                  </div>
                  <div className="text-[10px] text-muted-foreground">{r.segmento}</div>
                </TableCell>
                <TableCell className="text-xs">{r.fonte}</TableCell>
                <TableCell className="text-xs">{r.cidade}{r.estado ? `/${r.estado}` : ""}</TableCell>
                <TableCell className="text-xs font-mono">{r.similaridade_nome}%</TableCell>
                <TableCell className="text-xs font-mono">{r.similaridade_visual}%</TableCell>
                <TableCell><span className={`px-2 py-0.5 text-[10px] font-bold rounded-sm ${riskClass(r.grau_risco)}`}>{riskLabel(r.grau_risco)}</span></TableCell>
                <TableCell className="text-xs">{r.status}</TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => analyze(r.id)} title="Analisar com IA" data-testid={`analyze-${r.id}`}><Sparkle size={14} /></Button>
                    <Link to={`/app/resultados/${r.id}`}><Button size="sm" variant="ghost" data-testid={`view-result-${r.id}`}><Eye size={14} /></Button></Link>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && <TableRow><TableCell colSpan={9} className="text-center text-sm text-muted-foreground py-6">Nenhum resultado. Inicie uma varredura.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
